import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { DataContext } from "../../../context/DataContext";

import { getRol, getToken, getUser } from "../../../api/Account";
import { getCreditsCancelledAsync } from "../../../api/Report";
import { getGestoresAsync } from "../../../api/Users";
import { simpleMessage } from "../../../helpers/Helpers";
import { formatearFecha, formatearMonto } from "../../../helpers/formato";
import ReporteAgrupado from "../../../components/reportes/ReporteAgrupado";

const TODOS = "t";

const texto = (key, label, align = "left") => ({
  key,
  label,
  align,
  texto: true,
  valor: (c) => c[key],
});

const dinero = (key, label) => ({
  key,
  label,
  align: "center",
  total: true,
  formatoTotal: formatearMonto,
  valor: (c) => formatearMonto(c[key]),
  excel: (c) => c[key],
});

const fecha = (key, label) => ({
  key,
  label,
  align: "center",
  valor: (c) => formatearFecha(c[key]),
  excel: (c) => formatearFecha(c[key]),
});

const siNo = (key, label) => ({
  key,
  label,
  align: "center",
  valor: (c) => (c[key] ? "SI" : "NO"),
});

// Créditos cancelados en el rango. "Renovo": el cliente sacó otro crédito en el mismo rango.
// "Promedio" son los días de atraso promedio del cliente (proceso nocturno).
const COLUMNAS = [
  texto("fullName", "Cliente"),
  texto("phone", "Teléfono", "center"),
  texto("address", "Dirección"),
  { key: "creditId", label: "Crédito", align: "center", valor: (c) => c.creditId },
  dinero("montoEntregado", "M.Entregado"),
  {
    key: "interesCorriente",
    label: "I.C.",
    align: "center",
    valor: (c) => `${c.interesCorriente.toFixed(2)} %`,
    excel: (c) => c.interesCorriente,
  },
  texto("periocidad", "Perioc.", "center"),
  texto("plazo", "Plazo", "center"),
  dinero("montoRecibido", "M.Recibido"),
  fecha("fechaEntrega", "F.Entrega"),
  fecha("fechaVencimiento", "F.Venc."),
  fecha("fechaCancelacion", "F.Canc."),
  siNo("isNominal", "Nominal"),
  {
    key: "promedio",
    label: "Promedio",
    align: "center",
    valor: (c) => c.promedio.toFixed(2),
    excel: (c) => Number(c.promedio.toFixed(2)),
  },
  siNo("isRenovado", "Renovo"),
];

const CreditsCancelled = () => {
  const [data, setData] = useState([]);
  const [gestorName, setGestorName] = useState("Todos");

  const { fechaDesde, fechaHasta, selectedGestor, justNoReNew } = useParams();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  const manejarError = useCallback(
    (result) => {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      simpleMessage(result.error, "error");
    },
    [setIsUnautorized]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const [reporte, gestores] = await Promise.all([
        getCreditsCancelledAsync(token, {
          desde: fechaDesde.toString(),
          hasta: fechaHasta.toString(),
          gestorId: selectedGestor === TODOS ? 0 : selectedGestor,
          justNoRenovados: justNoReNew,
        }),
        selectedGestor === TODOS ? null : getGestoresAsync(token),
      ]);
      setIsLoading(false);

      if (!reporte.statusResponse) {
        manejarError(reporte);
        return;
      }
      setData(reporte.data.result);

      if (selectedGestor !== TODOS) {
        // Si la lista de gestores falla o no lo trae (dado de baja), el nombre sale del reporte.
        const gestor =
          gestores && gestores.statusResponse
            ? gestores.data.result.find((g) => g.id.toString() === selectedGestor)
            : null;
        setGestorName(
          gestor ? gestor.fullName : reporte.data.result[0]?.gestorNombre ?? selectedGestor
        );
      }
    })();
  }, [
    token,
    selectedGestor,
    fechaDesde,
    fechaHasta,
    justNoReNew,
    setIsLoading,
    manejarError,
  ]);

  // El servidor manda el nombre del gestor del crédito: también el de uno dado de baja.
  const agruparPor = useCallback((fila) => fila.gestorNombre, []);

  return (
    <ReporteAgrupado
      tituloReporte="Reporte de Créditos Cancelados"
      nombreArchivo="CreditosCancelados"
      parametros={[
        { label: "Gestor Seleccionado", valor: gestorName },
        { label: "Desde", valor: formatearFecha(fechaDesde) },
        { label: "Hasta", valor: formatearFecha(fechaHasta) },
        { label: "Solo no renovados", valor: justNoReNew === "true" ? "Sí" : "No" },
      ]}
      columnas={COLUMNAS}
      data={data}
      agruparPor={agruparPor}
      ordenInicial="fullName"
      contexto={{ title, slogan, icon, user, rol }}
    />
  );
};

export default CreditsCancelled;
